/**
 * Story 9.13 (AC6 / LI-8 / LI-9) — the ONE place the onboarding cohort rule and
 * the Screen-1 audience-copy variant live.
 *
 * Engagement is COHORT-based, not identity-based. The hard gate engages only for
 * the committed-to-selling cohort: a user who EITHER owns ≥1 listing in a real
 * selling state (`{active, on-hold, cancelled, sold}` → native
 * `attributes.state ∈ {published, closed}`; draft/pendingApproval excluded) OR
 * has a `cfPurchaseId` in privateData. A brand-new browser (no qualifying
 * listing, no CF purchase) is NEVER engaged.
 *
 * The listing leg is an async SDK query (it cannot be read from privateData), so
 * the caller resolves it and passes the boolean in as `hasSellerListing`
 * (queried with the `SELLER_LISTING_STATES` set below). This is what detects a
 * migrated seller without any privateData migration marker (OD-1 RESOLVED — no
 * marker exists and none is required).
 *
 * Absence of `cfPurchaseId` is used ONLY to pick Screen-1 copy AMONG the
 * already-engaged — never to gate engagement (LI-9).
 */

// Feeder-query state set for the listing leg. Covers active/on-hold/sold
// (native `published`, with the publicData.listingStatus overlay riding on top)
// and cancelled (native `closed`); excludes draft + pendingApproval. NOTE: the
// stock `fetchCurrentUserHasListings` queries `published` only and would MISS
// `closed` (sold/cancelled) — the gate needs this wider set.
export const SELLER_LISTING_STATES = 'published,closed';

const getPrivateData = currentUser => currentUser?.attributes?.profile?.privateData || {};
const getProtectedData = currentUser => currentUser?.attributes?.profile?.protectedData || {};

/**
 * True when the user has a ClickFunnels purchase marker in privateData (the
 * canonical CF cohort signal, present even before a first listing exists).
 * @param {Object} currentUser
 * @returns {boolean}
 */
export const hasCfPurchase = currentUser => !!getPrivateData(currentUser).cfPurchaseId;

/**
 * The cohort predicate that gates engagement (AC1/AC6): `hasSellerListing` OR
 * `cfPurchaseId`. `hasSellerListing` is resolved by the caller's feeder query.
 * @param {Object} currentUser
 * @param {{ hasSellerListing?: boolean }} resolved
 * @returns {boolean}
 */
export const isInOnboardingCohort = (currentUser, { hasSellerListing = false } = {}) => {
  if (!currentUser || !currentUser.id) {
    return false;
  }
  return !!hasSellerListing || hasCfPurchase(currentUser);
};

/**
 * Screen-1 audience-copy variant (placeholder copy keys, AC3). `cf` when a
 * `cfPurchaseId` is present, else `migrated` (engaged via a listing). Only ever
 * called for users already engaged by a positive signal.
 * @param {Object} currentUser
 * @returns {'cf'|'migrated'}
 */
export const onboardingAudience = currentUser => (hasCfPurchase(currentUser) ? 'cf' : 'migrated');

/**
 * Sum of idle (remaining, unspent) listing credits on the user. Handles BOTH
 * `privateData.listingCredits` shapes documented at tph-api
 * customersAirtableMirror.js — the Story 7.1 per-tier map
 * (`{ basic: 2, standard: 0, ... }`) and the legacy plain-number scalar.
 * The value is a mirror of the subscriptions truth, not the authority — a
 * cheap optimistic routing signal only, never a purchase gate (the wizard's
 * Case D gate re-checks the authoritative endpoint).
 *
 * Story 3.16 EXPORTED this (it was module-private) so `util/cfHandoff.js` can
 * COMPOSE it rather than re-implement the two-shape parsing. Exporting is
 * additive: `listYourHorseGoesToWizard` below is unchanged in behaviour, and
 * that is asserted (AC1.7). The mirror's known drift direction is toward
 * UNDER-reporting, which is why cfHandoff treats "mirror > 0 with a dead
 * endpoint" as evidence of a slot rather than evidence of none.
 * @param {Object} currentUser
 * @returns {number}
 */
export const idleCreditCount = currentUser => {
  const credits = getPrivateData(currentUser).listingCredits;
  if (typeof credits === 'number') return credits;
  if (credits && typeof credits === 'object') {
    return Object.values(credits).reduce((sum, v) => sum + (Number(v) || 0), 0);
  }
  return 0;
};

/**
 * "List your horse" routing predicate — the single source of truth behind the
 * <ListYourHorseLink> wrapper (and any config-driven call site). True routes to
 * the in-app listing wizard (NewListingPage — which is also the
 * continue-or-start page: the DraftResumeCard renders there when drafts
 * exist); false routes to the CF seller landing page.
 *
 * Rules, in order:
 *   1. Logged out → CF (false). An `isAuthenticated` user whose currentUser
 *      entity has not resolved yet is NOT logged out — safe default, wizard.
 *   2. ClickFunnels purchaser (`cfPurchaseId`) → wizard.
 *   3. Idle paid slot (`listingCredits` > 0) → wizard.
 *   4. Owns any listing or draft → wizard (continue-or-start).
 *   5. `hasAnyOwnListing` unresolved (null) → wizard. SAFE DEFAULT: a
 *      logged-in user is never routed toward a CF purchase until the listing
 *      query has POSITIVELY resolved to "nothing owned". The wizard is
 *      self-correcting (authoritative Case D slot gate, draft persistence,
 *      pendingDraftId return leg); a bare CF redirect is not.
 *   6. Positively resolved brand-new (no cfPurchaseId, 0 credits, query
 *      confirmed zero listings) → CF.
 *
 * @param {Object} currentUser
 * @param {Object} [resolved]
 * @param {boolean|null} [resolved.hasAnyOwnListing] `state.user.hasAnyOwnListing`
 *   — null while the query is unsettled (or failed; failure is non-fatal)
 * @param {boolean} [resolved.isAuthenticated] `state.auth.isAuthenticated` —
 *   available before currentUser resolves; guards the load-timing window
 * @returns {boolean}
 */
export const listYourHorseGoesToWizard = (
  currentUser,
  { hasAnyOwnListing = null, isAuthenticated = false } = {}
) => {
  if (!currentUser?.id) {
    // Authenticated but currentUser not yet resolved → logged-in with
    // unresolved data: fail toward the wizard, never toward CF.
    return !!isAuthenticated;
  }
  if (hasCfPurchase(currentUser)) return true;
  if (idleCreditCount(currentUser) > 0) return true;
  if (hasAnyOwnListing) return true;
  if (hasAnyOwnListing === null) return true;
  return false;
};

/**
 * The cheap "already done → skip the GET" short-circuit (AC1). Secondary to the
 * cohort check: it only matters for users already in cohort.
 * @param {Object} currentUser
 * @returns {boolean}
 */
export const isOnboardingComplete = currentUser =>
  !!getPrivateData(currentUser).onboardingCompletedAt;

/**
 * The known phone number to pre-fill Screen 2 with (AC4), if any.
 *
 * Prefers `privateData.phone` — the ALREADY-VERIFIED number written by the
 * onboarding/verify flow (or a migration). Falls back to
 * `protectedData.phoneNumber` — the E.164 number the seller entered at signup
 * (SignupForm/ConfirmSignupForm write it there, unverified), so a fresh
 * purchase-origin seller confirms their number instead of retyping it. The
 * value is only a pre-fill: it stays fully editable and is re-verified via
 * Twilio regardless of source, so an unverified fallback is safe. Returns
 * `undefined` when neither is present (field renders empty — no pre-fill).
 * @param {Object} currentUser
 * @returns {string|undefined}
 */
export const getKnownPhone = currentUser =>
  getPrivateData(currentUser).phone || getProtectedData(currentUser).phoneNumber || undefined;
